import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors";
const labelCls = "block text-[10px] tracking-[0.25em] text-white/50 mb-2";

const PlansEditor = () => {
  const { authHeaders } = useAuth();
  const { t } = useLang();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings/plans`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch((e) => console.error(e));
  }, []);

  const update = (i, field, val) =>
    setPlans((list) => list.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));

  const defaultFeatures = (p) => {
    const days = parseInt(p.name, 10);
    const f = t.plans.features;
    return [f.tips(Number.isNaN(days) ? p.name : days), f.analysis, f.odds, f.stake, f.support, f.replacement];
  };
  const featuresOf = (p) => (Array.isArray(p.features) ? p.features : defaultFeatures(p));
  const setFeature = (i, fi, val) =>
    update(i, "features", featuresOf(plans[i]).map((f, idx) => (idx === fi ? val : f)));
  const removeFeature = (i, fi) =>
    update(i, "features", featuresOf(plans[i]).filter((_, idx) => idx !== fi));
  const addFeature = (i) => update(i, "features", [...featuresOf(plans[i]), ""]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings/plans`, { plans }, { headers: authHeaders() });
      setPlans(data.plans);
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="plans-form" className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10">
      <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// SUBSCRIPTION OFFERS</div>
      <p className="text-xs text-white/50 mb-8">
        Membership plans shown on the homepage. Set an offer to NOT AVAILABLE to grey it out and disable its button.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {plans.map((p, i) => (
          <div
            key={p.key}
            data-testid={`plan-editor-${p.key}`}
            className={`border p-5 bg-black space-y-4 ${p.available ? "border-white/10" : "border-white/5 opacity-80"}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-[10px] tracking-[0.3em] text-white/50">OFFER {i + 1}</div>
              <div className="flex border border-white/15" role="group" aria-label="Availability">
                <button
                  type="button"
                  onClick={() => update(i, "available", true)}
                  data-testid={`plan-available-${p.key}`}
                  className={`px-3 py-1.5 text-[10px] font-bold tracking-[0.2em] transition-colors ${
                    p.available ? "bg-[#10B981] text-black" : "text-white/50 hover:text-white"
                  }`}
                >
                  AVAILABLE
                </button>
                <button
                  type="button"
                  onClick={() => update(i, "available", false)}
                  data-testid={`plan-unavailable-${p.key}`}
                  className={`px-3 py-1.5 text-[10px] font-bold tracking-[0.2em] transition-colors ${
                    !p.available ? "bg-[var(--hk-red)] text-white" : "text-white/50 hover:text-white"
                  }`}
                >
                  NOT AVAILABLE
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>NAME (BIG NUMBER)</label>
                <input value={p.name} onChange={(e) => update(i, "name", e.target.value)} required data-testid={`plan-name-${p.key}`} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>UNIT</label>
                <input value={p.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} placeholder="DAYS" data-testid={`plan-unit-${p.key}`} className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>PRICE (€)</label>
                <input value={p.price} onChange={(e) => update(i, "price", e.target.value)} required data-testid={`plan-price-${p.key}`} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>BADGE</label>
                <select
                  value={p.badge || ""}
                  onChange={(e) => update(i, "badge", e.target.value)}
                  data-testid={`plan-badge-${p.key}`}
                  className={`${inputCls} bg-black`}
                >
                  <option value="">None</option>
                  <option value="popular">Most Popular</option>
                  <option value="best">Best Value</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>DESCRIPTION (OPTIONAL — SHOWN UNDER PRICE)</label>
              <textarea
                value={p.description || ""}
                onChange={(e) => update(i, "description", e.target.value)}
                rows={3}
                data-testid={`plan-description-${p.key}`}
                className={`${inputCls} resize-y`}
              />
            </div>

            <div data-testid={`plan-features-${p.key}`}>
              <label className={labelCls}>FEATURES (ONE PER LINE — SHOWN AS LIST ON CARD)</label>
              <div className="space-y-2">
                {featuresOf(p).map((f, fi) => (
                  <div key={fi} className="flex gap-2">
                    <input
                      value={f}
                      onChange={(e) => setFeature(i, fi, e.target.value)}
                      placeholder="e.g. Full written analysis for every pick"
                      data-testid={`plan-feature-input-${p.key}-${fi}`}
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(i, fi)}
                      data-testid={`plan-feature-remove-${p.key}-${fi}`}
                      aria-label="Remove feature"
                      className="shrink-0 px-3 border border-white/15 text-white/50 hover:text-[var(--hk-red)] hover:border-[var(--hk-red)] text-xs transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addFeature(i)}
                  data-testid={`plan-feature-add-${p.key}`}
                  className="w-full border border-dashed border-white/20 py-2 text-[10px] tracking-[0.25em] text-white/60 hover:text-white hover:border-white/50 transition-colors"
                >
                  + ADD FEATURE
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="submit" disabled={busy} data-testid="plans-save-btn" className="btn-red px-8 py-3 text-[11px] font-bold tracking-[0.3em]">
        {busy ? "..." : t.admin.save}
      </button>
    </form>
  );
};

export default PlansEditor;
